import { withTimeout } from './withTimeout';

/* Signing the shared guest back in after the sign-up code screen - leaving it without an account, or after a
   customer sign-in it gave up on - so the storefront keeps working, without leaving a sign-in that cannot finish in
   charge of the session, and without two identities racing for it.

   What the libraries do, and why it matters here:
   - The store's socket sets no ack timeout. A request written to a transport that then dies never gets an answer:
     socket.io drops its ack and does not resend it. A request made while the socket is DOWN is held instead
     (sendBuffer) and sent on reconnect.
   - The auth client keeps the promise of the last sign-in as app.get('authentication'). Every service call waits on
     it (its client hook), and so does reAuthenticate() - which the store's own 'connect' handler runs on every
     reconnect, before anything registered later. A sign-in that will never answer, left there, holds all of them.
   - The server runs a connection's sign-ins side by side and keeps whichever finishes last.
   So:
     a customer sign-in given up on   written live, it died with its transport (or a live socket has had 30 s):
                                      dropped (authentication.reset). Held while offline, it is still to go out:
                                      it stays in charge, and decides on the reconnect.
     socket up                        the guest signs in now, bounded. A late answer still loads the session. No
                                      answer: dropped, and tried again at once if the socket came back meanwhile,
                                      otherwise on the next connect.
     socket down                      nothing is sent into it; on the next connect, see below.
     the next connect                 whatever is in charge settles first (the store's re-authentication, or the
                                      held customer sign-in, bounded); then the guest goes in - unless someone is
                                      signed in by now, or anyone signed in or out meanwhile (store.authEpoch): a new
                                      sign-up's code screen must never get a guest sign-in under its verify.
   restoreGuestSession resolves true when the guest session is in place, false when it was refused or put off; it
   never throws. */

/* A customer sign-in the screen may have to give up on, and what restoreGuestSession needs to know about it then. */
export const startSignIn = (store, email, password) => {
  const held = !store.app.io.connected;   //made while the socket is down: held, and sent on the reconnect
  const attempt = store.login(email, password);
  return { attempt, abandoned: { state: store.app.get('authentication'), held } };
};

export const restoreGuestSession = async (store, { email, password, abandoned } = {}) => {
  const { app } = store;
  if (abandoned && app.get('authentication') === abandoned.state) {
    //Still to go out, or just gone out on a reconnect during the wait: its answer decides (in the background - the
    //screen lets go now).
    if (abandoned.held) {
      if (app.io.connected) settleThenGuest(store, { email, password }, store.authEpoch);
      else putOffToConnect(store, { email, password });
      return false;
    }
    app.authentication.reset();
  }
  if (!app.io.connected) {
    putOffToConnect(store, { email, password });
    return false;
  }
  return signInNow(store, { email, password });
};

const signInNow = async (store, { email, password }, retried = false) => {
  const { app } = store;
  const socketId = app.io.id;
  const attempt = store.login(email, password);
  const pending = app.get('authentication');   //store.login calls app.authenticate synchronously: this is its promise
  try {
    await withTimeout(attempt);
  } catch (error) {
    console.log('error while signing back in as the guest: ', error);
    if (error?.data?.reason !== 'timeout') return false;
    //A late answer still signs the guest in: the session loads then.
    attempt.then(() => store.setObservables()).catch(() => {});
    if (app.get('authentication') !== pending) return false;   //someone else has signed in since
    app.authentication.reset();
    //The socket came back during the wait (a new connection): the sign-in died with the old one - again, now.
    if (app.io.connected && app.io.id !== socketId && !retried) return signInNow(store, { email, password }, true);
    putOffToConnect(store, { email, password });
    return false;
  }
  try {
    await withTimeout(store.setObservables());
  } catch (error) {
    console.log('error while loading the guest session: ', error);
  }
  return true;
};

const putOffToConnect = (store, { email, password }) => {
  const epoch = store.authEpoch;
  store.app.io.once('connect', () => settleThenGuest(store, { email, password }, epoch));
};

//Whatever is in charge settles first - on a connect, the store's own 'connect' handler (it ran first) is
//re-authenticating, or a sign-in held while offline has just gone out - and then the guest, if still wanted.
const settleThenGuest = async (store, { email, password }, epoch) => {
  const { app } = store;
  await withTimeout(Promise.resolve(app.get('authentication'))).catch(() => {});
  if (store.authEpoch !== epoch || app.authentication.authenticated) return false;
  return restoreGuestSession(store, { email, password });
};
